import { useState, useEffect } from 'react';
import { Table, Button, Input, Select, Space, Notification, Modal } from '@douyinfe/semi-ui';
import { IconPlus, IconSearch, IconEdit, IconDelete, IconEyeOpened, IconEyeClosed } from '@douyinfe/semi-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import request from '../../utils/request';
import ProductCategorySelector from '../../components/ProductCategorySelector';

const Products = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // 统一解析 URL 参数
    const searchParams = new URLSearchParams(location.search);
    const with_tags = searchParams.get('with_tags') || 0;
    const without_tags = searchParams.get('without_tags') || 0;

    useEffect(() => {
        fetchData(1);
    }, [location.search]);

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 20,
        total: 0
    });
    const [searchKey, setSearchKey] = useState('');
    const [status, setStatus] = useState(null);
    const [categoryUid, setCategoryUid] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const fetchData = async (page = 1) => {
        setLoading(true);
        try {
            const res = await request.get('/go/sp/shop/list_product/', {
                params: {
                    page: page - 1,
                    limit: pagination.pageSize,
                    status: status === null ? -1 : status,
                    key: searchKey,
                    cat_uid: categoryUid,
                    with_tags,
                    without_tags
                }
            });
            if (res.errno === 0) {
                setDataSource(res.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: res.total_cnt || 0
                }));
            } else {
                setDataSource([]);
                setPagination(prev => ({
                    ...prev,
                    currentPage: 1,
                    total: 0
                }));
                Notification.error({
                    title: '获取商品列表失败',
                    content: res.errstr || '请稍后重试'
                });
            }
        } catch (error) {
            setDataSource([]);
            setPagination(prev => ({
                ...prev,
                currentPage: 1,
                total: 0
            }));
            Notification.error({
                title: '获取商品列表失败',
                content: '请稍后重试'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (uids) => {
        try {
            const res = await request.post('/go/sp/shop/delete_product/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (res.errno === 0) {
                Notification.success({
                    title: '删除成功',
                    content: '商品已成功删除'
                });
                setSelectedRowKeys([]);
                fetchData(pagination.currentPage);
            } else {
                Notification.error({
                    title: '删除失败',
                    content: res.errstr || '请稍后重试'
                });
            }
        } catch (error) {
            Notification.error({
                title: '删除失败',
                content: '请稍后重试'
            });
        }
    };

    const handleBatchEdit = async (uids, newStatus) => {
        try {
            const res = await request.post('/go/sp/shop/batchedit_product/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status: newStatus
            });
            if (res.errno === 0) {
                Notification.success({
                    title: '操作成功',
                    content: '商品状态已更新'
                });
                setSelectedRowKeys([]);
                fetchData(pagination.currentPage);
            } else {
                Notification.error({
                    title: '操作失败',
                    content: res.errstr || '请稍后重试'
                });
            }
        } catch (error) {
            Notification.error({
                title: '操作失败',
                content: '请稍后重试'
            });
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const statusMap = {
        0: { text: '正常', type: 'success' },
        1: { text: '下架', type: 'danger' },
    };

    const columns = [
        {
            title: '商品主图',
            dataIndex: 'main_img',
            width: 100,
            render: (text) => text ? <img src={text} alt="商品图片" style={{ width: 50, height: 50, objectFit: 'cover' }} /> : '-'
        },
        {
            title: '商品信息',
            dataIndex: 'title',
            width: 300,
            render: (text, record) => (
                <div>
                    <div>{text}</div>
                    <div style={{ color: 'var(--semi-color-text-2)', fontSize: '12px' }}>{record.title2}</div>
                    {record.buy_limit > 0 && (
                        <div style={{ color: 'var(--semi-color-warning)', fontSize: '12px' }}>限购{record.buy_limit}件</div>
                    )}
                </div>
            )
        },
        {
            title: '分类',
            dataIndex: 'cat.name',
            width: 150,
        },
        {
            title: '价格',
            dataIndex: 'price',
            width: 100,
            render: (text) => `¥${(text / 100).toFixed(2)}`
        },
        {
            title: '库存',
            dataIndex: 'quantity',
            width: 120,
            render: (text, record) => (
                <div>
                    <div>{text}</div>
                    {record.quantity_warn && text <= record.quantity_warn && (
                        <div style={{ color: 'var(--semi-color-danger)', fontSize: '12px' }}>库存不足</div>
                    )}
                </div>
            )
        },
        {
            title: '评价',
            dataIndex: 'comment_cnt',
            width: 100,
            render: (text) => text || 0
        },
        {
            title: '销量',
            dataIndex: 'sell_cnt',
            width: 100,
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button theme="borderless" type="primary" icon={<IconEdit />} onClick={() => navigate(`/products/edit/${record.uid}`)}>编辑</Button>
                    <Button
                        theme="borderless"
                        type="warning"
                        icon={record.status === 0 ? <IconEyeClosed /> : <IconEyeOpened />}
                        onClick={() => handleBatchEdit(record.uid, record.status === 0 ? 1 : 0)}
                    >
                        {record.status === 0 ? '下架' : '上架'}
                    </Button>
                    <Button
                        theme="borderless"
                        type="danger"
                        icon={<IconDelete />}
                        onClick={() => {
                            Modal.confirm({
                                title: '确认删除',
                                content: '确定要删除这个商品吗？此操作不可撤销。',
                                onOk: () => handleDelete(record.uid)
                            });
                        }}
                    >
                        删除
                    </Button>
                </Space>
            )
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Input
                        style={{ width: 200 }}
                        prefix={<IconSearch />}
                        placeholder="搜索商品"
                        showClear
                        value={searchKey}
                        onChange={value => setSearchKey(value)}
                    />
                    <ProductCategorySelector
                        value={categoryUid}
                        onChange={setCategoryUid}
                        placeholder="商品分类"
                        style={{ width: 200 }}
                    />
                    <Select
                        style={{ width: 150 }}
                        placeholder="商品状态"
                        value={status}
                        onChange={value => setStatus(value)}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        <Select.Option value={0}>正常</Select.Option>
                        <Select.Option value={1}>下架</Select.Option>
                    </Select>
                    <Button
                        type="primary"
                        icon={<IconSearch />}
                        onClick={() => fetchData(1)}
                        loading={loading}
                    >
                        搜索
                    </Button>
                    <Button
                        type="primary"
                        icon={<IconPlus />}
                        onClick={() => {
                            if (with_tags && with_tags !== "0") {
                                navigate(`/products/add?tags=${encodeURIComponent(with_tags)}`);
                            } else {
                                navigate('/products/add');
                            }
                        }}
                    >
                        新增商品
                    </Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button
                                type="warning"
                                icon={<IconEyeClosed />}
                                onClick={() => handleBatchEdit(selectedRowKeys, 1)}
                            >
                                批量下架
                            </Button>
                            <Button
                                type="primary"
                                icon={<IconEyeOpened />}
                                onClick={() => handleBatchEdit(selectedRowKeys, 0)}
                            >
                                批量上架
                            </Button>
                            <Button
                                type="danger"
                                icon={<IconDelete />}
                                onClick={() => {
                                    Modal.confirm({
                                        title: '确认删除',
                                        content: `确定要删除选中的 ${selectedRowKeys.length} 个商品吗？此操作不可撤销。`,
                                        onOk: () => handleDelete(selectedRowKeys)
                                    });
                                }}
                            >
                                批量删除
                            </Button>
                        </Space>
                    )}
                </Space>
            </div>
            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                rowKey="uid"
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: page => fetchData(page)
                }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (selectedRowKeys) => {
                        setSelectedRowKeys(selectedRowKeys);
                    },
                    getCheckboxProps: (record) => ({
                        disabled: false
                    })
                }}
                rowClassName={(record) => record.status === 1 ? 'semi-table-row-disabled' : ''}
                scroll={{ x: 1200 }}
            />
        </div>
    );
};

export default Products;